import type { StatusTone } from '@/demo/procurement/components/Badges';
import type { StatTone } from '@/demo/procurement/components/StatTile';
import type { ScriptedStep } from '@/demo/procurement/components/useScriptedSteps';
import type {
  ContractId,
  MaterialId,
  PersonId,
  SupplierId,
} from '@/demo/procurement/data/types';

export const AGENT_IDS = [
  'director',
  'intake',
  'onboarding',
  'contracts',
  'compliance',
  'sourcing',
  'negotiation',
  'followup',
  'risk',
  'portal',
  'demand',
  'planner',
] as const;

export type AgentId = (typeof AGENT_IDS)[number];

export function isAgentId(value: string | undefined): value is AgentId {
  return AGENT_IDS.some((id) => id === value);
}

export type AutonomyLevel = 'suggest' | 'approval' | 'autonomous';

export const AUTONOMY_LEVELS: AutonomyLevel[] = [
  'suggest',
  'approval',
  'autonomous',
];

export type AgentStatus = 'active' | 'shadow' | 'paused';

export type AgentChannel =
  | 'email'
  | 'teams'
  | 'portal'
  | 'edi'
  | 'phone'
  | 'erp'
  | 'qms';

/** Pages other areas own that an agent's work is visible on. */
export type PageKey =
  | 'requests'
  | 'contracts'
  | 'invoice-compliance'
  | 'risk'
  | 'buyer-board'
  | 'replenishment'
  | 'purchase-orders'
  | 'forecast-summary'
  | 'forecast-bom'
  /** The agents' team channel view on the AI agents page. */
  | 'agents-channel';

/** A deep link target, always by shared id. */
export type EntityRef =
  | { kind: 'invoice'; id: string }
  | { kind: 'supplier'; id: SupplierId }
  | { kind: 'contract'; id: ContractId; clause?: string }
  | { kind: 'request'; id: string }
  | { kind: 'risk'; id: string }
  | { kind: 'sourcing'; id: string }
  | { kind: 'onboarding'; id: string }
  | { kind: 'po'; id: string }
  | { kind: 'material'; id: MaterialId }
  /** A forecast series id, `${sku}@${siteId}`. */
  | { kind: 'forecast'; id: string }
  | { kind: 'page'; page: PageKey; label: string };

/** How a limit value prints: dollars, percent, or a bare number. */
export type LimitUnit = 'usd' | 'pct' | 'num';

export type GuardrailControl =
  /** A policy the admin can switch on or off. */
  | { type: 'rule'; enabled: boolean }
  /** A hard limit that cannot be switched off. */
  | { type: 'hard' }
  /** A numeric threshold the admin can change. */
  | {
      type: 'limit';
      unit: LimitUnit;
      value: number;
      min: number;
      max: number;
    };

export interface Guardrail {
  id: string;
  /** The sentence, or the part before the value for a limit. */
  before: string;
  /** The part after the value for a limit. */
  after?: string;
  control: GuardrailControl;
}

export interface AgentMetrics30d {
  actions: number;
  /** Share of outputs people accepted without editing, percent. */
  acceptancePct: number;
  hoursSaved: number;
  /** Actions that needed a person to approve them. */
  approvalsRequired: number;
  /** A short headline number and what it counts, e.g. $612K / leakage prevented. */
  impact: { value: string; label: string };
}

export interface YtdStat {
  label: string;
  value: string;
}

export interface AgentLink {
  label: string;
  ref: EntityRef;
}

export interface AgentDef {
  id: AgentId;
  name: string;
  /** One line for the roster. */
  short: string;
  description: string;
  channels: AgentChannel[];
  defaultAutonomy: AutonomyLevel;
  defaultStatus: AgentStatus;
  /** Set when the agent starts in practice mode, explains why. */
  shadowNote?: string;
  sopId: string;
  metrics: AgentMetrics30d;
  ytd: YtdStat[];
  /** The pages where this agent's work shows up. */
  where: AgentLink[];
  guardrails: Guardrail[];
  /** Set on the coordinator: the agents it assigns work to. */
  coordinates?: AgentId[];
}

export interface SopStep {
  title: string;
  instruction: string;
  /** Tools and data the step uses. */
  uses: string[];
}

export interface Sop {
  id: string;
  agentId: AgentId;
  title: string;
  version: number;
  ownerId: PersonId;
  /** Absolute ISO date: an SOP is a static document. */
  lastReviewed: string;
  steps: SopStep[];
}

export type ReasoningKind = 'read' | 'check' | 'policy' | 'action';

export interface ReasoningStep {
  kind: ReasoningKind;
  text: string;
  /** What the step looked at, as a link. */
  source?: EntityRef;
}

export type Approver =
  | { kind: 'autonomous' }
  | { kind: 'approved'; personId: PersonId }
  | { kind: 'pending'; personId: PersonId }
  | { kind: 'config'; personId: PersonId };

export interface ActivityEntry {
  id: string;
  agentId: AgentId;
  /** Epoch milliseconds. */
  atMs: number;
  action: string;
  targets: EntityRef[];
  detail?: string;
  outcome: { label: string; tone: StatusTone };
  approver: Approver;
  reasoning: ReasoningStep[];
}

export interface ReplayStat {
  label: string;
  value: string;
  sub?: string;
  tone?: StatTone;
}

export interface ReplayDef {
  /** The button label, e.g. "Run on last quarter's 2,140 invoices". */
  cta: string;
  /** One sentence: what gets replayed, in practice mode. */
  description: string;
  steps: ScriptedStep[];
  stats: ReplayStat[];
  findings: string[];
  verdict: string;
}
